import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { loadRawAccount, RawAccount } from '../utils/runeStore';
import { Slot } from './simShared';
import { artsOfRaw, mergeArts, buildFromReplayUnit } from './TacticBuild';

/**
 * **공덱 룬작에 내 룬 맞추기** — [학습기록](AI 가상 룬)·[전투기록](실전 룬)으로 불러온 공덱의 룬작에 계정 JSON 의 실제 룬·아티·유물을 맞춰 끼운다
 * (사용자 2026-10-27 "ai가 맞춰온 공덱 룬에 맞게 룬 아티 자동할당", 초안 2안).
 *
 * **공략 [내 룬으로 맞추기](RuneFit)와 같은 길** (사용자 2026-10-29 "시뮬레이션에서 내 룬 자동할당해줄 때도 공략과 마찬가지로 룬이 세팅되게해줘. 전투기록에서도 마찬가지고") —
 * 지금 공덱 칸의 능력치·세트·아티 부옵만 보낸다(학습 기록 룬 목표 deck.targets 는 안 보낸다). 서버(swSim engine/src/accountfit.ts)가
 * 그 숫자에 가장 가깝게(near — 룬 고급 설정과 같은 점수) 보관함 + 모든 몹이 낀 룬에서 세 몹이 겹치지 않게 고르고(리더 칸 먼저),
 * 아티·유물은 가장 필요한 주옵·부옵 쪽으로 하나씩 고른다.
 * 몹은 게임 자료 맨몸(6성 40) 그대로 — 보유몹은 보지 않는다. 계정 JSON 은 **룬 옵티마이저 탭에서 불러온 것**을 쓴다(그 탭이 이 브라우저에
 * 원본도 같이 담는다, utils/runeStore gaRaw). 맞출 때만 서버로 보낸다(서버는 저장하지 않는다).
 *
 * 누르는 단추 [내 룬 자동할당]은 고른 몹 룬 상자의 [룬 변경] 왼쪽에 있다 — SimPage 가 BuildRunes extraAction 으로 넣고 이 fit() 을 부른다.
 * 맞춘 결과는 칸의 룬 별·상세로만 보인다(AI 룬작 대비 표·[AI 룬으로 되돌리기]는 뺐다, 사용자 2026-10-27 "이 부분은 표시하지말자"). 여기는 오류만 보인다.
 */
export interface SimFitRunesHandle { fit: () => void }
interface FitMob { found: boolean; raw?: any }

/** 맞춘 몹 원본 → 칸 (룬 별·상세는 BuildRunes 가 raw 로 그린다). 유물도 서버가 고른 것 (전투기록 불러오기 SimPage loadBattle 과 같은 모양) */
export function slotFromRaw(s: Slot, raw: any): Slot {
    const build = buildFromReplayUnit(raw);
    if (!build) return s;
    const arts = artsOfRaw(raw);
    const relics = (raw.relics || []).map((r: any) => (r?.sec_effect || []).slice(0, 3).map(Number) as [number, number, number]).filter((r: number[]) => r.length === 3);
    return { ...s, build, sets: build.sets, art: mergeArts(arts), arts, relics, raw };
}

/** 칸 → 맞출 능력치 (룬 설정이 있으면 그 합계, 없으면 칸 기본 능력치 — 공략 RuneFit statsOf 와 같은 이름) */
function statsOfSlot(s: Slot): Record<string, number> {
    const b = s.build;
    if (b) return { hp: b.hp, atk: b.atk, def: b.def, spd: b.spd, cri_rate: b.cr, cri_dmg: b.cd, res: b.res, acc: b.acc };
    const st = s.stats;
    return st ? { hp: st.hp, atk: st.atk, def: st.def, spd: st.spd, cri_rate: st.critRate, cri_dmg: st.critDamage, res: st.resistance, acc: st.accuracy } : {};
}
/** 칸 → 아티 부옵 합 (원본이 있으면 원본에서) */
function artOfSlot(s: Slot): Record<number, number> {
    return s.raw ? mergeArts(artsOfRaw(s.raw)) : s.arts ? mergeArts(s.arts) : s.art || {};
}

/** 서버로 보낼 계정 — 룬·아티 창고만 (보관함 + 모든 몹이 낀 것). 몹은 게임 자료 맨몸이라 보유몹은 안 보낸다 */
export function accountPayload(a: RawAccount) {
    return {
        // 몹은 번호·새긴 유물만 — 같은 몹이 있으면 그 유물을 쓴다(룬피팅 탭과 같다)
        units: a.units.map((u) => ({ unit_id: u.unit_id, unit_master_id: u.unit_master_id, unit_level: u.unit_level, class: u.class, runes: [], artifacts: [], relics: u.relics || [] })),
        relics: [...(a.relics || []), ...a.units.flatMap((u) => u.relics || [])],
        runes: [...a.runes, ...a.units.flatMap((u) => u.runes || [])],
        artifacts: [...(a.artifacts || []), ...a.units.flatMap((u) => u.artifacts || [])]
    };
}

const SimFitRunes = forwardRef<SimFitRunesHandle, {
    /** 지금 공덱 칸 셋 — 이 능력치·세트·아티에 맞추고, 맞춘 룬을 이 칸에 끼운다 */
    slots: Slot[];
    isMobile: boolean;
    onApply: (slots: Slot[]) => void;
    /** 맞추는 중 — [내 룬 자동할당] 단추 글자 */
    onBusy: (busy: boolean) => void;
}>(function SimFitRunes({ slots, isMobile, onApply, onBusy }, ref) {
    const [busy, setBusyState] = useState(false);
    const setBusy = (b: boolean) => { setBusyState(b); onBusy(b); };
    const [err, setErr] = useState('');

    const fit = async () => {
        // 이 화면을 연 뒤에 룬 옵티마이저 탭에서 불러왔을 수 있다 — 누를 때 읽는다
        const a = await loadRawAccount();
        if (!a) { setErr('룬 옵티마이저 탭에서 계정 JSON 을 먼저 불러와 주세요 (예전에 불러왔다면 한 번 더 불러와야 합니다)'); return; }
        if (slots.length !== 3 || slots.some((s) => !s.pick)) { setErr('공덱 세 칸을 먼저 채워 주세요'); return; }
        setBusy(true); setErr('');
        try {
            const ids = slots.map((s) => Number(s.unit?.unitMasterId) || 0);
            const r = await fetch('/api/sim/fit', {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({
                    deck: {
                        ...(ids.every((x) => x > 0) ? { units: ids } : { mobs: slots.map((s) => ({ name: s.pick!.name, attribute: s.pick!.attribute })) }),
                        stats: slots.map(statsOfSlot), sets: slots.map((s) => s.build?.sets || s.sets || []), art: slots.map(artOfSlot)
                    },
                    account: accountPayload(a)
                })
            });
            const d = await r.json();
            if (!d?.success) throw new Error(d?.error || '룬을 맞추지 못했습니다');
            const mobs: FitMob[] = Array.isArray(d.mobs) ? d.mobs : [];
            if (mobs.length !== 3) throw new Error('시뮬레이션 서버가 결과를 주지 않았습니다');
            onApply(slots.map((s, i) => (mobs[i]?.found && mobs[i].raw ? slotFromRaw(s, mobs[i].raw) : s)));
        } catch (e: any) {
            setErr(e.message || '룬을 맞추지 못했습니다');
        }
        setBusy(false);
    };

    useImperativeHandle(ref, () => ({ fit: () => { if (!busy) fit(); } }));

    if (!err) return null;
    return <div style={{ textAlign: 'center', marginBottom: 10, color: '#d32f2f', fontSize: isMobile ? 11 : 12, fontWeight: 700 }}>{err}</div>;
});
export default SimFitRunes;
